import moment from 'moment'
import getUser from '@/api/auth'
import { postTermsAccepted } from '@/api/accounts'
import { toggleCopyOnBody } from '@/util/copypaste_helpers'
import { normalizeUTCTimeFromLocalTz } from '@/util/time'
import { UserTypes } from '@/util/usertypes'

export const LICENSE_SUB_TYPES = {
  PREMIUM: 'premium',
  GOLD: 'gold',
  TRIAL: 'trial',
}

export default {
  namespaced: true,
  state: {
    fetching: true, // the first thing we want to do when the store is initialized is signal that we are fetching user auth info
    user: {},
    user_type: UserTypes.types.DEFAULT,
    admin_schools: [],
    toolkit_account: {},
    license: {},
    super_admin: false,
    pathways_access: false,
    domains: [],
    schools: [],
    domain_upp: false,
    _enableFullstory: false,
    showLicenseSelector: false,
    _callbackQueue: [], // callbacks for when user info is fetched; each one called with true if user was authenticated, otherwise false
    terms_accepted_error: false,
    browserStorage: {},
    diagnostics: {},
  },
  mutations: {
    setUserInfo(state, payload) {
      state.user = payload.user || {}
      state.user_type = payload.user_type || ''
      state.admin_schools = payload.admin_schools || []
      state.toolkit_account = payload.toolkit_account || {}
      state.license = payload.license || {}
      state.super_admin = payload.super_admin
      state.pathways_access = payload.pathways_access
      state.domains = payload.domains || []
      state.schools = payload.schools || []
      state.domain_upp = payload.domain_upp
      state._enableFullstory = payload.enable_fullstory
    },
    setFetching(state, flag) {
      state.fetching = flag
    },
    pushOnetimeCallback(state, callback) {
      state._callbackQueue.push(callback)
    },
    clearCallbackQueue(state) {
      state._callbackQueue = []
    },
    setLicense(state, payload) {
      state.license = Object.assign({}, state.license, payload)
    },
    setLicenseSelectorOpen(state, flag) {
      state.showLicenseSelector = flag
    },
    setTermsAcceptedError(state, bool) {
      state.terms_accepted_error = bool
    },
    setTermsAccepted(state, user) {
      state.user = { ...state.user, terms_accepted_at: user.terms_accepted_at }
    },
    setBrowserStorage(state, itemToSet) {
      const newStorage = { ...state.browserStorage, ...itemToSet }
      localStorage.setItem('gb_user', JSON.stringify(newStorage))
      state.browserStorage = newStorage
    },
    setDiagnostics(state, diagnostics) {
      state.diagnostics = diagnostics
    },
  },
  actions: {
    getUserInfo(context) {
      context.commit('setFetching', true)
      const currentStorage = JSON.parse(localStorage.getItem('gb_user')) || {}
      context.commit('setBrowserStorage', currentStorage)

      return getUser()
        .then((data) => {
          context.commit('setUserInfo', data)
          context.commit('contentAccess/setContentAccessReport', data.content_access_report, {
            root: true,
          })
          context.commit('contentAccess/setLibraryQuotasFromTkAccount', data.toolkit_account, {
            root: true,
          })
          context.dispatch('storeAndSetUserContentProfile', data.content_profile)
          context.commit('setFetching', false)

          // get user download history
          context.dispatch('contentAccess/getUserDownloads', null, { root: true })

          // init the app
          context.dispatch('initAuthenticatedTracking', data, { root: true })
          // run onetime callbacks, then clear stack
          context.state._callbackQueue.forEach((cb) => {
            cb(true)
          })
          context.commit('clearCallbackQueue')
          return data
        })
        .catch(({ status, responseJSON, responseText }) => {
          console.info(`Could not authenticate user`)
          context.dispatch(
            'initUnauthenticatedTracking',
            { enable_fullstory: true },
            { root: true }
          )
          context.dispatch('storeAndSetUserContentProfile', responseJSON.content_profile)
          context.commit('setFetching', false)
          // run onetime callbacks, then clear stack
          context.state._callbackQueue.forEach((cb) => {
            cb(false)
          })
          context.commit('clearCallbackQueue')
          return responseJSON
        })
    },
    storeAndSetUserContentProfile({ rootGetters, commit, dispatch }, profileInfo) {
      commit('contentProfile/storeProfile', profileInfo, {
        root: true,
      })

      // if we haven't set the "active" content profile yet, initialize it here. We need to make sure it's not set first, in case it's been set elsewhere in the app and thus shouldn't be overwritten
      if (!rootGetters['contentProfile/profileId']) {
        dispatch('contentProfile/setActiveProfileById', profileInfo.id, {
          root: true,
        })
      }
    },
    acceptTerms(context) {
      return postTermsAccepted()
        .then((res) => {
          context.commit('setTermsAcceptedError', false)
          context.commit('setTermsAccepted', res.user)
        })
        .catch((err) => {
          console.error(err)
          context.commit('setTermsAcceptedError', true)
        })
    },
    setStorage(context, itemToSet) {
      context.commit('setBrowserStorage', itemToSet)
    },
  },
  getters: {
    licenseSubType(state) {
      return state.license.sub_type
    },
    isAuthenticated(state) {
      return !!(state.user && state.user.uuid)
    },
    isFetching(state) {
      return state.fetching
    },
    isTeks(state, getters, rootState, rootGetters) {
      return (
        UserTypes.checks.isUserTypeTeks(state.user_type) || getters.userContentProfileId === 'texas'
      )
    },
    isDefault(state) {
      return UserTypes.checks.isUserTypeDefault(state.user_type)
    },
    isSuperAdmin(state) {
      // in general we want to disable users from copying the entire page, but we'll allow it for super admins
      if (!state.super_admin) {
        toggleCopyOnBody(false)
      } else {
        toggleCopyOnBody(true)
      }
      return state.super_admin
    },
    hasLicense(state) {
      return !!(state.license && state.license.uuid && state.license.uuid.length > 0)
    },
    isTrial(state) {
      return state.license && state.license.sub_type === LICENSE_SUB_TYPES.TRIAL
    },
    isPremium(state) {
      return (
        state.license &&
        (state.license.sub_type === LICENSE_SUB_TYPES.PREMIUM ||
          state.license.sub_type === LICENSE_SUB_TYPES.GOLD)
      )
    },
    isEnterprise(state) {
      return state.license && state.license.type === 'enterprise'
    },
    isIndividual(state) {
      return state.license && state.license.type === 'individual'
    },
    isSchoolAdmin(state) {
      return state.admin_schools && state.admin_schools.length > 0
    },
    daysLeftInLicense(state, getters) {
      return Math.max(0, getters.licenseEndDateFromToday)
    },
    licenseEndDateFromToday(state, getters) {
      if (getters.isSuperAdmin || !getters.isAuthenticated || !getters.hasLicense) {
        return NaN
      }
      var today = moment(normalizeUTCTimeFromLocalTz())
      var ed = moment(state.license.end_date)

      return ed.diff(today, 'days')
    },
    isEnterpriseEndingSoon(state, getters) {
      return getters.isEnterprise && getters.isPremium && getters.daysLeftInLicense <= 60
    },
    isLicenseExpired(state, getters) {
      return getters.hasLicense && getters.licenseEndDateFromToday < 0
    },
    authInfo(state, getters) {
      return {
        isAuthenticated: getters.isAuthenticated,
        isTrial: getters.isTrial,
        isPremium: getters.isPremium,
        isSuperAdmin: getters.isSuperAdmin,
        isLicenseExpired: getters.isLicenseExpired,
      }
    },
    userContentProfileId(state) {
      return state.user.content_profile_id
    },
    userId(state) {
      return state.user.uuid
    },
    domainInfo(state) {
      return (state.domains && state.domains[0]) || {}
    },
    isTestUser(state) {
      return state.user.email && state.user.email.indexOf('test.k12.ca.us') > -1
    },
    isGoalbookBusinessUser(state) {
      return (
        state.domains &&
        state.domains.map((domain) => domain.name).indexOf('Goalbook Business') > -1
      )
    },
    isMultiyear(state) {
      return state.domains.some((domain) => domain.flags.includes('multiyear'))
    },
    hasGrowAccessTag(state) {
      return (state.user.tags || []).some((tag) => tag.toLowerCase() === 'grow access')
    },
    hasThreadsAccessOverrideTag(state) {
      return (state.user.tags || []).some(
        (tag) => tag.toLowerCase() === 'threads access (flag override)'
      )
    },
    interactiveGuidesOn(state) {
      return state.domains.some((domain) => domain.flags.includes('interactive_guides_on'))
    },
    eventMetadata(state, getters) {
      return {
        u: state.user.uuid || '',
        ue: state.user.email || '',
        un: state.user.name || '',
        ut: state.user.title || '',
        utz: state.user.timezone || '',
        role: state.user.role || '',
        ust: state.user.state || '',
        d: (state.domains && state.domains[0] && state.domains[0].uuid) || '',
        dn: (state.domains && state.domains[0] && state.domains[0].name) || '',
        s: (state.schools && state.schools.map((s) => s.uuid)) || [],
        sn: (state.schools && state.schools.map((s) => s.name)) || [],
        lu: state.license.uuid || '',
        lt: state.license.type || '',
        lst: state.license.sub_type || '',
        lsd: state.license.start_date || null,
        led: state.license.end_date || null,
        le: getters.isLicenseExpired,
      }
    },
    userDomainUuid(state) {
      return (state.domains && state.domains[0] && state.domains[0].uuid) || ''
    },
    userName(state) {
      return state.user.name
    },
    termsNotAccepted(state) {
      return !Boolean(state.user.terms_accepted_at)
    },
    diagnostics(state) {
      return state.diagnostics || {}
    },
  },
}
